import React, { useState, useCallback, useRef, useEffect } from "react"
import * as Icons from "lucide-react"

export default function EmailSearch({ onSearch, onClose }) {
  const [query, setQuery] = useState("")
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState({
    sender: "",
    recipient: "",
    has_attachments: false,
    is_flagged: false,
    is_starred: false,
    is_unread: false,
    date_from: "",
    date_to: "",
  })
  const inputRef = useRef(null)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const handleSearch = useCallback(() => {
    const activeFilters = {}
    Object.entries(filters).forEach(([key, val]) => {
      if (val) activeFilters[key] = val
    })
    onSearch(query, activeFilters)
  }, [query, filters, onSearch])

  const handleKeyDown = e => {
    if (e.key === "Enter") handleSearch()
    if (e.key === "Escape") onClose()
  }

  const clearFilters = () => {
    setFilters({
      sender: "",
      recipient: "",
      has_attachments: false,
      is_flagged: false,
      is_starred: false,
      is_unread: false,
      date_from: "",
      date_to: "",
    })
    setQuery("")
    onSearch("", {})
  }

  return (
    <div className="em-search-overlay" onClick={onClose}>
      <div className="em-search-panel" onClick={e => e.stopPropagation()}>
        <div className="em-search-header">
          <Icons.Search size={16} />
          <input
            ref={inputRef}
            placeholder="Search by sender, subject, or keywords..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <button
            className="em-search-filter-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Icons.SlidersHorizontal size={16} />
          </button>
          <button className="em-search-close" onClick={onClose}>
            <Icons.X size={16} />
          </button>
        </div>

        {showFilters && (
          <div className="em-search-filters">
            <div className="em-search-filters-grid">
              <div className="em-search-filter-field">
                <label>Sender</label>
                <input
                  placeholder="From"
                  value={filters.sender}
                  onChange={e =>
                    setFilters(p => ({ ...p, sender: e.target.value }))
                  }
                />
              </div>
              <div className="em-search-filter-field">
                <label>Recipient</label>
                <input
                  placeholder="To"
                  value={filters.recipient}
                  onChange={e =>
                    setFilters(p => ({ ...p, recipient: e.target.value }))
                  }
                />
              </div>
              <div className="em-search-filter-field">
                <label>From Date</label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={e =>
                    setFilters(p => ({ ...p, date_from: e.target.value }))
                  }
                />
              </div>
              <div className="em-search-filter-field">
                <label>To Date</label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={e =>
                    setFilters(p => ({ ...p, date_to: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="em-search-filter-toggles">
              <label className="em-search-toggle">
                <input
                  type="checkbox"
                  checked={filters.has_attachments}
                  onChange={e =>
                    setFilters(p => ({
                      ...p,
                      has_attachments: e.target.checked,
                    }))
                  }
                />
                <Icons.Paperclip size={12} />
                Has Attachments
              </label>
              <label className="em-search-toggle">
                <input
                  type="checkbox"
                  checked={filters.is_flagged}
                  onChange={e =>
                    setFilters(p => ({ ...p, is_flagged: e.target.checked }))
                  }
                />
                <Icons.Flag size={12} />
                Flagged
              </label>
              <label className="em-search-toggle">
                <input
                  type="checkbox"
                  checked={filters.is_starred}
                  onChange={e =>
                    setFilters(p => ({ ...p, is_starred: e.target.checked }))
                  }
                />
                <Icons.Star size={12} />
                Starred
              </label>
              <label className="em-search-toggle">
                <input
                  type="checkbox"
                  checked={filters.is_unread}
                  onChange={e =>
                    setFilters(p => ({ ...p, is_unread: e.target.checked }))
                  }
                />
                <Icons.MailOpen size={12} />
                Unread
              </label>
            </div>
            <div className="em-search-filter-actions">
              <button className="em-search-apply-btn" onClick={handleSearch}>
                Search
              </button>
              <button className="em-search-clear-btn" onClick={clearFilters}>
                Clear
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
